import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useAuthHook } from "@/hooks/useAuth";
import logo from "../../assets/logo.png";
import { Property } from "../../data/models";

interface HeaderProps {
  theme: "light" | "dark";
  setTheme: (theme: "light" | "dark") => void;
  logout: () => void;
}

export default function Header({ theme, setTheme, logout }: HeaderProps) {
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userName, setUserName] = useState<string>("CLIENT");
  const location = useLocation();
  const pathname = location.pathname;
  const profileRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [clientProperty, setClientProperties] = useState<Property[]>([]);
  const [isLoadingProperties, setIsLoadingProperties] = useState(true);
  const { user, isAuthenticated } = useAuthHook();

  /* ================= THEME SYNC ================= */
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      root.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [theme]);

  const apiUrl =
    import.meta.env.VITE_API_BASE_URL ||
    "https://valuate-api.vercel.app/api/v1";

  // Fetch client properties from API
  useEffect(() => {
    const fetchClientProperties = async () => {
      if (!user?.id) return;

      try {
        setIsLoadingProperties(true);
        setError(null);

        const token = localStorage.getItem("valuation_token");
        const response = await fetch(
          `${apiUrl}/properties/by-upi?upi=${encodeURIComponent(user.upi)}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch properties: ${response.status}`);
        }

        const data = await response.json();

        if (data.success && data.data) {
          setClientProperties(data.data);
          setUserName(data.data.ownerName || "CLIENT");
        } else {
          setClientProperties([]);
        }
      } catch (err) {
        console.error("Error fetching properties:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load properties",
        );
      } finally {
        setIsLoadingProperties(false);
      }
    };

    if (isAuthenticated && user?.id) {
      fetchClientProperties();
    }
  }, [user?.id, isAuthenticated, apiUrl]);

  // Load user data from localStorage on component mount
  useEffect(() => {
    try {
      const userData = localStorage.getItem("valuation_user");
      if (userData) {
        const user = JSON.parse(userData);
        const displayName = user.upi || user.role || "CLIENT";
        setUserName(displayName);
      }
    } catch (error) {
      console.error("Error reading user data from localStorage:", error);
      setUserName("CLIENT");
    }
  }, []);

  /* ================= ACTIVE LINK ================= */
  const isActive = (href: string) => {
    if (href === "/client-dashboard") {
      return pathname === href;
    }
    return pathname.startsWith(href);
  };

  /* ================= OUTSIDE CLICK ================= */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileRef.current &&
        !profileRef.current.contains(event.target as Node)
      ) {
        setProfileMenuOpen(false);
      }
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node)
      ) {
        setMobileMenuOpen(false);
      }
    };

    if (profileMenuOpen || mobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileMenuOpen, mobileMenuOpen]);

  // Navigation links for client dashboard
  const navLinks = [
    { href: "/client-dashboard", label: "Dashboard" },
    { href: "/client-dashboard/my-properties", label: "My Properties" },
    { href: "/client-dashboard/submit-property", label: "Submit Property" },
    { href: "/client-dashboard/reports", label: "Reports" },
  ];

  return (
    <header className="z-50 bg-white dark:bg-gray-900  dark:border-gray-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <div className="flex items-center justify-between">
          {/* LOGO */}
          <Link to="/client-dashboard" className="flex items-center space-x-2 flex-shrink-0">
            <div className="inline-flex items-center bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 justify-center">
              <img
                src={logo}
                alt="Valuatify Logo"
                className="h-12 rounded-lg"
              />
            </div>
            <span className="hidden sm:block text-sm text-gray-500 dark:text-white">
              Real-time Property Valuation
            </span>
          </Link>

          {/* DESKTOP NAVIGATION */}

          {/* RIGHT SIDE */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* THEME TOGGLE */}
            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="p-2 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? "☀️" : "🌙"}
            </button>

            {/* DESKTOP PROFILE MENU */}
            <div className="hidden sm:block relative" ref={profileRef}>
              <button
                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                className="flex items-center text-sm rounded-md px-3 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
              >
                <span className="mr-2">👤</span>
                <span className="hidden md:inline">
                  {userName}
                </span>
                <span className="ml-1">▼</span>
              </button>

              {profileMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                  {["Profile Settings", "Account Security", "Preferences"].map(
                    (item) => (
                      <div
                        key={item}
                        className="px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                      >
                        {item}
                      </div>
                    )
                  )}
                  <div className="border-t border-gray-200 dark:border-gray-700"></div>
                  <div
                    onClick={() => {
                      logout();
                      window.location.href = "/login";
                    }}
                    className="px-4 py-2 text-sm cursor-pointer text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Logout
                  </div>
                </div>
              )}
            </div>

            {/* MOBILE MENU BUTTON */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label="Toggle menu"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {mobileMenuOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* MOBILE MENU */}
        {mobileMenuOpen && (
          <div
            ref={mobileMenuRef}
            className="lg:hidden border-t border-gray-200 dark:border-gray-700 py-2"
          >
            <nav className="flex flex-col space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    isActive(link.href)
                      ? "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* MOBILE PROFILE SECTION */}
            <div className="sm:hidden border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
              <div className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-white flex items-center">
                <span className="mr-2 text-2xl">👤</span>
                <span>{userName}</span>
              </div>
              {["Profile Settings", "Account Security", "Preferences"].map(
                (item) => (
                  <div
                    key={item}
                    className="px-3 py-2 text-sm cursor-pointer text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    {item}
                  </div>
                )
              )}
              <div
                onClick={() => {
                  logout();
                  window.location.href = "/login";
                }}
                className="px-3 py-2 text-sm cursor-pointer text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Logout
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}